import download from 'downloadjs'
import { ofType } from 'redux-observable'
import { forkJoin, from, merge, of, race } from 'rxjs'
import {
  catchError,
  filter,
  map,
  mergeMap,
  reduce,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators'
import { fromSocketEvent } from 'services/socket'
import { SIMULATIONS_EXPORT_ERROR, SIMULATIONS_EXPORT_READY } from 'services/socket/socketEvents'
import { v4 as uuidv4 } from 'uuid'

import { combineEpicsIsolated } from 'utils/epicRegistry'
import { xhrExtractFileName } from 'utils/helpers'
import { URL_CONST } from 'utils/urlConsts'
import { matchURL } from 'utils/urls'

import {
  COMPANY_FILTER_CHANGE,
  FETCH_SELECTED_COMPANY_DATA_ASYNC,
} from 'containers/PartnerOptions/constants'
import { selectCompanyFilter } from 'containers/PartnerOptions/selectors'
import { addToast } from 'containers/Toasts/actions'

import ToastErrorMessage from 'components/pages/SpearPhishingAgent/components/Dashboard/components/CardSection/components/ToastErrorMessage'
import ToastSuccessMessage from 'components/pages/SpearPhishingAgent/components/Dashboard/components/CardSection/components/ToastSuccessMessage'
import { trackPsimaSimulationLaunch } from 'components/pages/SpearPhishingAgent/psimaPendo'

import {
  ALL_RISK_LEVEL_VALUE,
  CAMPAIGNS_PAGE_SIZE,
  EXPORT_SIMULATIONS_CSV_ASYNC,
  GET_CAMPAIGNS_LIST_ASYNC,
  GET_EMAIL_PREVIEW_ASYNC,
  GET_LEVEL_COUNTS_ASYNC,
  GET_METRIC_BANNER_ASYNC,
  GET_MONITORING_STATUS_INFO_ASYNC,
  GET_PARTICIPANT_DETAILS_ASYNC,
  GET_PSIMA_COMPANIES_ASYNC,
  GET_SIMULATION_LIST_ASYNC,
  GET_SIMULATIONS_META_ASYNC,
  LAUNCH_SOURCE_VALUES,
  LOAD_MORE_CAMPAIGNS,
  PAGINATE_SIMULATIONS,
  POST_CARD_FEEDBACK_ASYNC,
  POST_EMAIL_FEEDBACK_ASYNC,
  SEND_SIMULATIONS_ASYNC,
  SEND_SIMULATIONS_BATCH_SIZE,
  SET_CARDS_ORDERING,
  SET_PLACEHOLDER_FILTER,
  SET_RISK_LEVEL_SELECTED_FILTERS,
  SET_SIMULATION_DATE_RANGE,
  SET_SIMULATION_DEPARTMENTS,
  SET_SIMULATION_SORT,
  SET_STATUS_SELECTED_FILTERS,
  SIMULATIONS_PAGE_SIZE,
} from './constants'
import {
  selectCampaignsData,
  selectCardsOrdering,
  selectExcludedParticipants,
  selectPlaceholderFilter,
  selectRiskLevelSelectedFilters,
  selectSimulationData,
  selectSingleCampaignId,
} from './selectors'
import { buildSimulationListQuery, isSimulationPageGone, SIMULATIONS_FALLBACK_PAGE } from './utils'

// Empty for the All Companies sentinel, which buildURL drops so the backend falls back
// to the caller's own company.
const companyIdOf = (state$) => selectCompanyFilter(state$.value)?.value || ''

// PSIMA epics are registered statically (ui/src/epics.js), so without this an unguarded
// refetch fires PSIMA requests from every page in the product on every tenant switch.
const allowOnRoute = (route) => (action) =>
  action.type !== FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS || matchURL(route)

// Drops a response that lands after the tenant changed. switchMap only supersedes a
// request the same epic re-issues, and the route guard can stop that re-issue.
const sameTenant = (state$) => {
  const companyId = companyIdOf(state$)
  return () => companyIdOf(state$) === companyId
}

const getPsimaCompaniesEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_PSIMA_COMPANIES_ASYNC.INITIATE),
    switchMap(() =>
      api.spearPhishingAgent.getPsimaCompanies().pipe(
        map((response) => GET_PSIMA_COMPANIES_ASYNC.onSuccess({ data: response })),
        catchError((error) =>
          of(epicsErrorHandler(GET_PSIMA_COMPANIES_ASYNC.onError, error, [403]))
        ),
        takeUntil(action$.pipe(ofType(GET_PSIMA_COMPANIES_ASYNC.CANCEL)))
      )
    )
  )

const getMonitoringStatusInfoEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_MONITORING_STATUS_INFO_ASYNC.INITIATE, FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS),
    filter(allowOnRoute(URL_CONST.psima.self)),
    switchMap(() =>
      api.spearPhishingAgent.getMonitoringInfo(companyIdOf(state$)).pipe(
        map((response) =>
          GET_MONITORING_STATUS_INFO_ASYNC.onSuccess({
            data: response,
          })
        ),
        catchError((error) =>
          of(epicsErrorHandler(GET_MONITORING_STATUS_INFO_ASYNC.onError, error, [403]))
        ),
        filter(sameTenant(state$)),
        takeUntil(action$.pipe(ofType(GET_MONITORING_STATUS_INFO_ASYNC.CANCEL)))
      )
    )
  )

const getCampaignsListEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      GET_CAMPAIGNS_LIST_ASYNC.INITIATE,
      SET_RISK_LEVEL_SELECTED_FILTERS,
      SET_PLACEHOLDER_FILTER,
      SET_CARDS_ORDERING,
      GET_LEVEL_COUNTS_ASYNC.SUCCESS,
      LOAD_MORE_CAMPAIGNS
    ),
    filter((action) => {
      if (action.type === SET_PLACEHOLDER_FILTER && action.payload?.skipFetch) return false
      if (action.type === SET_RISK_LEVEL_SELECTED_FILTERS && action.payload?.skipFetch) return false
      return true
    }),
    switchMap((action) => {
      const campaignData = selectCampaignsData(state$.value)
      const { page } = campaignData
      const riskLevelFilters = selectRiskLevelSelectedFilters(state$.value)
      const placeholderFilter = selectPlaceholderFilter(state$.value)
      const ordering = selectCardsOrdering(state$.value)
      const isAllRiskLevelsSelected =
        !riskLevelFilters.length || riskLevelFilters.includes(ALL_RISK_LEVEL_VALUE)
      const isPreselectAllCampaigns =
        action.type !== SET_RISK_LEVEL_SELECTED_FILTERS && isAllRiskLevelsSelected
      const filters = isAllRiskLevelsSelected ? undefined : riskLevelFilters

      return api.spearPhishingAgent
        .getCampaignsList(
          filters,
          page,
          CAMPAIGNS_PAGE_SIZE,
          { ...placeholderFilter, ordering },
          companyIdOf(state$)
        )
        .pipe(
          map((response) =>
            GET_CAMPAIGNS_LIST_ASYNC.onSuccess({
              data: response,
              isPreselectAllCampaigns,
              page,
            })
          ),
          catchError((error) =>
            of(epicsErrorHandler(GET_CAMPAIGNS_LIST_ASYNC.onError, error, [403]))
          ),
          filter(sameTenant(state$)),
          takeUntil(action$.pipe(ofType(GET_CAMPAIGNS_LIST_ASYNC.CANCEL)))
        )
    })
  )

const sendSimulationsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(SEND_SIMULATIONS_ASYNC.INITIATE),
    switchMap(() => {
      const campaignData = selectCampaignsData(state$.value)
      const excludedParticipants = selectExcludedParticipants(state$.value)
      const singleCampaignId = selectSingleCampaignId(state$.value)
      const simulations = singleCampaignId
        ? [
            {
              cardId: singleCampaignId,
              participantIds: campaignData.items
                .find(({ id: campaignId }) => campaignId === singleCampaignId)
                .audience.suggestedProfiles.reduce((acc, profile) => {
                  if (excludedParticipants[singleCampaignId]?.includes(profile.id)) {
                    return acc
                  }

                  return [...acc, profile.id]
                }, []),
            },
          ]
        : campaignData.selectedCampaigns
            .reduce((acc, id) => {
              const campaign = campaignData.items.find(({ id: campaignId }) => campaignId === id)

              if (!campaign) {
                return acc
              }

              return [
                ...acc,
                {
                  cardId: campaign.id,
                  participantIds: campaign.audience.suggestedProfiles.reduce((acc, profile) => {
                    if (excludedParticipants[campaign.id]?.includes(profile.id)) {
                      return acc
                    }

                    return [...acc, profile.id]
                  }, []),
                },
              ]
            }, [])
            .filter(({ participantIds }) => participantIds.length > 0)
      const launchSource = singleCampaignId
        ? LAUNCH_SOURCE_VALUES.MANUAL
        : LAUNCH_SOURCE_VALUES.QUICK_LAUNCH
      const launchBatchId = uuidv4()
      const simulationsChunks = []

      if (!singleCampaignId && simulations.length > SEND_SIMULATIONS_BATCH_SIZE) {
        for (let index = 0; index < simulations.length; index += SEND_SIMULATIONS_BATCH_SIZE) {
          simulationsChunks.push(simulations.slice(index, index + SEND_SIMULATIONS_BATCH_SIZE))
        }
      }
      const totalChunks = simulationsChunks.length || 1
      // Bound once: chunks are chained with mergeMap, so re-reading the state$ would let a
      // switch mid-launch post tenant A's card ids under tenant B.
      const launchCompanyId = companyIdOf(state$)
      const sendSimulationsRequest = (simulationsChunk) =>
        api.spearPhishingAgent.sendSimulations(
          {
            simulations: simulationsChunk,
            launchSource,
            launchBatchId,
            totalChunks,
          },
          launchCompanyId
        )
      const sendSimulations$ = simulationsChunks.length
        ? simulationsChunks
            .slice(1)
            .reduce(
              (request$, simulationsChunk) =>
                request$.pipe(mergeMap(() => sendSimulationsRequest(simulationsChunk))),
              sendSimulationsRequest(simulationsChunks[0])
            )
        : sendSimulationsRequest(simulations)

      return sendSimulations$.pipe(
        reduce(
          (acc, response) => {
            const results = response?.data?.results
            if (Array.isArray(results)) {
              acc.results.push(...results)
            }
            acc.lastResponse = response
            return acc
          },
          { results: [], lastResponse: null }
        ),
        mergeMap(({ results, lastResponse }) => {
          const successfulResults = results.filter(
            ({ status, simulationId }) => status === 'success' && simulationId
          )
          const batchSize = successfulResults.length

          successfulResults.forEach(({ simulationId, cardId }) => {
            trackPsimaSimulationLaunch({
              simulationId,
              launchSource,
              batchSize,
              isZeroEdit: !excludedParticipants[cardId]?.length,
            })
          })

          return of(
            SEND_SIMULATIONS_ASYNC.onSuccess({ data: lastResponse }),
            addToast({
              id: 'send-simulations-success',
              ds: true,
              message: <ToastSuccessMessage />,
              type: 'success',
              timeToDisappear: 10000,
            })
          )
        }),
        catchError((error) =>
          of(
            epicsErrorHandler(SEND_SIMULATIONS_ASYNC.onError, error, [403]),
            addToast({
              id: 'send-simulations-error',
              ds: true,
              message: <ToastErrorMessage />,
            })
          )
        ),
        takeUntil(action$.pipe(ofType(SEND_SIMULATIONS_ASYNC.CANCEL)))
      )
    })
  )

const getSimulationListEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      GET_SIMULATION_LIST_ASYNC.INITIATE,
      SET_STATUS_SELECTED_FILTERS,
      SET_SIMULATION_DEPARTMENTS,
      SET_SIMULATION_DATE_RANGE,
      SET_SIMULATION_SORT,
      PAGINATE_SIMULATIONS,
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS
    ),
    filter(allowOnRoute(URL_CONST.psima.launched)),
    filter((action) => {
      if (action.type !== PAGINATE_SIMULATIONS) return true
      const { page: currentPage } = selectSimulationData(state$.value)
      return action.payload.page !== currentPage
    }),
    filter((action) => {
      if (action.type !== SET_SIMULATION_DATE_RANGE && action.type !== SET_SIMULATION_SORT) {
        return true
      }
      return Boolean(selectSimulationData(state$.value).loader)
    }),
    switchMap((action) => {
      const simulationData = selectSimulationData(state$.value)
      const targetPage =
        action.type === PAGINATE_SIMULATIONS ? action.payload.page : simulationData.page
      const listQuery = buildSimulationListQuery(simulationData)

      const listErrorOf = (error) =>
        of(epicsErrorHandler(GET_SIMULATION_LIST_ASYNC.onError, error, [403]))

      const fetchPage = (page) =>
        api.spearPhishingAgent
          .getSimulationList(listQuery, page, SIMULATIONS_PAGE_SIZE, companyIdOf(state$))
          .pipe(map((response) => GET_SIMULATION_LIST_ASYNC.onSuccess({ data: response, page })))

      return fetchPage(targetPage).pipe(
        catchError((error) => {
          if (isSimulationPageGone(error) && targetPage !== SIMULATIONS_FALLBACK_PAGE) {
            return fetchPage(SIMULATIONS_FALLBACK_PAGE).pipe(catchError(listErrorOf))
          }
          return listErrorOf(error)
        }),
        filter(sameTenant(state$)),
        takeUntil(action$.pipe(ofType(GET_SIMULATION_LIST_ASYNC.CANCEL)))
      )
    })
  )

const getLevelCountsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      GET_LEVEL_COUNTS_ASYNC.INITIATE,
      SEND_SIMULATIONS_ASYNC.SUCCESS,
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS
    ),
    filter(allowOnRoute(URL_CONST.psima.self)),
    switchMap(() =>
      api.spearPhishingAgent.getLevelCounts(companyIdOf(state$)).pipe(
        map((response) => GET_LEVEL_COUNTS_ASYNC.onSuccess({ data: response })),
        catchError((error) => of(epicsErrorHandler(GET_LEVEL_COUNTS_ASYNC.onError, error, [403]))),
        filter(sameTenant(state$)),
        takeUntil(action$.pipe(ofType(GET_LEVEL_COUNTS_ASYNC.CANCEL)))
      )
    )
  )

const getMetricBannerEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_METRIC_BANNER_ASYNC.INITIATE, FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS),
    filter(allowOnRoute(URL_CONST.psima.self)),
    switchMap(() =>
      api.spearPhishingAgent.getMetricBanner(companyIdOf(state$)).pipe(
        map((response) => GET_METRIC_BANNER_ASYNC.onSuccess({ data: response })),
        catchError((error) => of(epicsErrorHandler(GET_METRIC_BANNER_ASYNC.onError, error, [403]))),
        filter(sameTenant(state$)),
        takeUntil(action$.pipe(ofType(GET_METRIC_BANNER_ASYNC.CANCEL)))
      )
    )
  )

/* The endpoint paginates at 100. A simulation's roster is tens of people, and the drawer's
   sorting and filtering only mean something over the whole roster, so every page is fetched
   and handed over as one list. Rosters above one page are the exception, not the design. */
const PARTICIPANTS_PAGE_SIZE = 100

const getParticipantDetailsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_PARTICIPANT_DETAILS_ASYNC.INITIATE),
    mergeMap((action) => {
      const companyId = companyIdOf(state$)
      return api.spearPhishingAgent
        .getParticipantDetails(action.payload.id, companyId, 1, PARTICIPANTS_PAGE_SIZE)
        .pipe(
          mergeMap((firstPage) => {
            const totalPages = firstPage?.meta?.totalPages ?? 1
            if (totalPages <= 1) return of(firstPage)
            const rest = Array.from({ length: totalPages - 1 }, (_, index) =>
              api.spearPhishingAgent.getParticipantDetails(
                action.payload.id,
                companyId,
                index + 2,
                PARTICIPANTS_PAGE_SIZE
              )
            )
            return forkJoin(rest).pipe(
              map((pages) => ({
                ...firstPage,
                data: [firstPage, ...pages].flatMap((page) => page?.data || []),
              }))
            )
          }),
          map((response) =>
            GET_PARTICIPANT_DETAILS_ASYNC.onSuccess({ data: response, id: action.payload.id })
          ),
          catchError((error) =>
            of(
              /* The failure must say which simulation it belongs to, so the drawer can show
                 the notice on that roster alone and keep the loader list honest. */
              epicsErrorHandler(
                (details) => ({
                  ...GET_PARTICIPANT_DETAILS_ASYNC.onError(details),
                  payload: { id: action.payload.id },
                }),
                error,
                [403]
              )
            )
          ),
          filter(sameTenant(state$)),
          takeUntil(action$.pipe(ofType(GET_PARTICIPANT_DETAILS_ASYNC.CANCEL)))
        )
    })
  )

const getEmailPreviewEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_EMAIL_PREVIEW_ASYNC.INITIATE),
    switchMap((action) =>
      api.spearPhishingAgent
        .getEmailPreview(action.payload.id, action.payload.participantId, companyIdOf(state$))
        .pipe(
          map((response) => GET_EMAIL_PREVIEW_ASYNC.onSuccess({ data: response })),
          catchError((error) =>
            of(epicsErrorHandler(GET_EMAIL_PREVIEW_ASYNC.onError, error, [403]))
          ),
          filter(sameTenant(state$)),
          takeUntil(action$.pipe(ofType(GET_EMAIL_PREVIEW_ASYNC.CANCEL)))
        )
    )
  )

const postEmailFeedbackEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(POST_EMAIL_FEEDBACK_ASYNC.INITIATE),
    switchMap((action) =>
      api.spearPhishingAgent
        .postEmailFeedback(
          action.payload.id,
          action.payload.participantId,
          { reviewSide: action.payload.feedback },
          companyIdOf(state$)
        )
        .pipe(
          mergeMap((response) =>
            of(
              POST_EMAIL_FEEDBACK_ASYNC.onSuccess({ data: response }),
              addToast({
                id: 'post-email-feedback-success',
                ds: true,
                message: 'Thanks, your feedback is on its way to our team.',
                type: 'success',
              })
            )
          ),
          catchError((error) =>
            of(epicsErrorHandler(POST_EMAIL_FEEDBACK_ASYNC.onError, error, [403]))
          ),
          takeUntil(action$.pipe(ofType(POST_EMAIL_FEEDBACK_ASYNC.CANCEL)))
        )
    )
  )

const postCardFeedbackEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(POST_CARD_FEEDBACK_ASYNC.INITIATE),
    mergeMap((action) => {
      const body = { reviewSide: action.payload.reviewSide }

      if (action.payload.comment !== undefined) {
        body.comment = action.payload.comment
      }

      return api.spearPhishingAgent
        .postCardFeedback(action.payload.cardId, body, companyIdOf(state$))
        .pipe(
          map((response) => POST_CARD_FEEDBACK_ASYNC.onSuccess({ data: response })),
          catchError((error) =>
            of(epicsErrorHandler(POST_CARD_FEEDBACK_ASYNC.onError, error, [403]))
          ),
          takeUntil(action$.pipe(ofType(POST_CARD_FEEDBACK_ASYNC.CANCEL)))
        )
    })
  )

const getSimulationsMetaEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_SIMULATIONS_META_ASYNC.INITIATE, FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS),
    filter(allowOnRoute(URL_CONST.psima.launched)),
    switchMap(() =>
      api.spearPhishingAgent.getSimulationsMeta(companyIdOf(state$)).pipe(
        filter(sameTenant(state$)),
        map((response) => GET_SIMULATIONS_META_ASYNC.onSuccess({ data: response })),
        catchError((error) =>
          of(epicsErrorHandler(GET_SIMULATIONS_META_ASYNC.onError, error, [403]))
        ),
        takeUntil(action$.pipe(ofType(GET_SIMULATIONS_META_ASYNC.CANCEL)))
      )
    )
  )

const EXPORT_TOAST_ID = 'psima-launched-csv'
const EXPORT_FAILED_MESSAGE = "Couldn't export the CSV."
const CSV_EXPORT_FALLBACK_FILENAME = 'launched-simulations.csv'
const ZIP_EXPORT_FALLBACK_FILENAME = 'launched-simulations.zip'

const buildExportToast = (message, type) =>
  addToast({ id: EXPORT_TOAST_ID, ds: true, type, message })

const buildExportFailedActions = () =>
  of(EXPORT_SIMULATIONS_CSV_ASYNC.onError({}), buildExportToast(EXPORT_FAILED_MESSAGE))

const saveExportFileAndReport = (response, fallbackFilename) => {
  download(response.response, xhrExtractFileName(response.xhr) || fallbackFilename)
  return of(
    EXPORT_SIMULATIONS_CSV_ASYNC.onSuccess(),
    buildExportToast('CSV downloaded.', 'success')
  )
}

const readExportJobId = (response) =>
  from(response.response.text()).pipe(map((text) => JSON.parse(text).jobId))

const awaitExportJob = (jobId, companyId, api) => {
  const isThisJob = (payload) => payload?.jobId === jobId
  const ready$ = fromSocketEvent(SIMULATIONS_EXPORT_READY).pipe(
    filter(isThisJob),
    take(1),
    mergeMap(() => api.spearPhishingAgent.downloadSimulationsExport(jobId, companyId)),
    mergeMap((fileResponse) => saveExportFileAndReport(fileResponse, ZIP_EXPORT_FALLBACK_FILENAME))
  )
  const failed$ = fromSocketEvent(SIMULATIONS_EXPORT_ERROR).pipe(
    filter(isThisJob),
    take(1),
    mergeMap(buildExportFailedActions)
  )

  return merge(of(buildExportToast('CSV export started.', 'info')), race(ready$, failed$))
}

const exportSimulationsCsvEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(EXPORT_SIMULATIONS_CSV_ASYNC.INITIATE),
    switchMap((action) => {
      const companyId = companyIdOf(state$)
      return api.spearPhishingAgent.exportSimulations(action.payload.body, companyId).pipe(
        mergeMap((response) => {
          if (response.status === 200) {
            return saveExportFileAndReport(response, CSV_EXPORT_FALLBACK_FILENAME)
          }
          if (response.status !== 202) return buildExportFailedActions()

          return readExportJobId(response).pipe(
            mergeMap((jobId) =>
              jobId ? awaitExportJob(jobId, companyId, api) : buildExportFailedActions()
            )
          )
        }),
        catchError((error) =>
          of(
            epicsErrorHandler(EXPORT_SIMULATIONS_CSV_ASYNC.onError, error, [403]),
            buildExportToast(EXPORT_FAILED_MESSAGE)
          )
        ),
        filter(sameTenant(state$)),
        takeUntil(action$.pipe(ofType(EXPORT_SIMULATIONS_CSV_ASYNC.CANCEL)))
      )
    })
  )

// Only the reads the refetch does NOT re-initiate. On COMPANY_FILTER_CHANGE,
// not ...SUCCESS: getCompanyData runs first, leaving these live on tenant A.
const cancelPsimaRequestsOnCompanyChangeEpic = (action$) =>
  action$.pipe(
    ofType(COMPANY_FILTER_CHANGE),
    mergeMap(() =>
      of(
        GET_CAMPAIGNS_LIST_ASYNC.cancel(),
        GET_EMAIL_PREVIEW_ASYNC.cancel(),
        GET_PARTICIPANT_DETAILS_ASYNC.cancel(),
        EXPORT_SIMULATIONS_CSV_ASYNC.cancel()
      )
    )
  )

export default combineEpicsIsolated(
  getPsimaCompaniesEpic,
  cancelPsimaRequestsOnCompanyChangeEpic,
  getMonitoringStatusInfoEpic,
  getCampaignsListEpic,
  sendSimulationsEpic,
  getSimulationListEpic,
  exportSimulationsCsvEpic,
  getLevelCountsEpic,
  getMetricBannerEpic,
  getParticipantDetailsEpic,
  getEmailPreviewEpic,
  postEmailFeedbackEpic,
  postCardFeedbackEpic,
  getSimulationsMetaEpic
)
